import React, { useEffect, useState } from 'react';
import {
    LuArrowRightLeft,
    LuCalendarSync,
    LuChevronDown,
    LuChevronRight,
    LuCircleCheckBig,
    LuFileInput,
    LuFileOutput,
    LuLoader,
    LuMail,
    LuMessageSquare,
    LuPackageOpen,
    LuPenTool,
    LuPlay,
    LuSettings2,
    LuStickyNote,
    LuTriangleAlert,
    LuUsers,
    LuZap,
} from 'react-icons/lu';

import { FeatureFlag, TenantTeamMember } from '@ndvr/auth-api';
import { type Calendar, type GUID, NoteTemplateType } from '@ndvr/customer-api';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { getHouseholdName } from 'utils';
import { cn } from 'utils/cn';
import {
    useEmbedDocument,
    useGenerateWritingStyle,
    useHouseholdsForTenant,
    useImportRedtailData,
    useListCalendars,
    useMigrateEmails,
    useMigrateNotes,
    useMigrateTextMessages,
    useMoveAccountGroupAcrossHouseholds,
    useOnboardSingleAccountFromHouseholdOnboarding,
    useRedtailExportTenant,
    useRegenerateMeetingNote,
    useStartIngestion,
    useSyncCalendar,
    useSyncCalendarEvent,
    useToggleFeatureFlag,
    useTriggerBoxIntake,
    useTriggerHouseholdOnboarding,
    useTriggerProspectOnboarding,
} from 'views/admin/dev-queries';
import { useTenantTeamQuery } from 'views/admin/tenantTeam/tenant-team-queries';
import { useAuthContext } from 'views/auth/Auth';
import { useFeatureFlag } from 'views/auth/useFeatureFlag';

// ─── Badge ───────────────────────────────────────────────────────────────────

type BadgeType =
    | 'routine'
    | 'per-tenant'
    | 'all-tenants'
    | 'per-document'
    | 'irreversible'
    | 'long-running'
    | 'confirm-required';

const BADGE_CONFIG: Record<BadgeType, { label: string; className: string }> = {
    routine: { label: 'Routine', className: 'bg-gray-100 text-gray-600 border-gray-200' },
    'per-tenant': { label: 'Per tenant', className: 'bg-gray-100 text-gray-600 border-gray-200' },
    'all-tenants': { label: 'All tenants', className: 'bg-gray-100 text-gray-600 border-gray-200' },
    'per-document': { label: 'Per document', className: 'bg-gray-100 text-gray-600 border-gray-200' },
    irreversible: { label: 'Irreversible', className: 'bg-amber-100 text-amber-700 border-amber-200' },
    'long-running': { label: 'Long-running', className: 'bg-amber-100 text-amber-700 border-amber-200' },
    'confirm-required': { label: 'Confirm required', className: 'bg-red-100 text-red-700 border-red-200' },
};

const ControlBadge = ({ type }: { type: BadgeType }) => {
    const { label, className } = BADGE_CONFIG[type];
    return (
        <span
            className={cn(
                'inline-flex items-center rounded border px-1.5 py-0.5 text-[10px] font-medium whitespace-nowrap',
                className,
            )}
        >
            {label}
        </span>
    );
};

// ─── ConfirmBar ───────────────────────────────────────────────────────────────

interface ConfirmBarProps {
    message: string;
    onConfirm: () => void;
    onCancel: () => void;
    isPending?: boolean;
}

const ConfirmBar = ({ message, onConfirm, onCancel, isPending }: ConfirmBarProps) => (
    <div className="mt-3 flex items-center gap-3 rounded-md border border-amber-200 bg-amber-50 px-4 py-3">
        <LuTriangleAlert className="h-4 w-4 flex-shrink-0 text-amber-600" />
        <p className="flex-1 text-sm text-amber-800">{message}</p>
        <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={onCancel} disabled={isPending}>
                Cancel
            </Button>
            <Button variant="destructive" size="sm" onClick={onConfirm} disabled={isPending}>
                {isPending && <LuLoader className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
                Confirm
            </Button>
        </div>
    </div>
);

// ─── SuccessBar ───────────────────────────────────────────────────────────────

const SuccessBar = ({ message, onDismiss }: { message: string; onDismiss: () => void }) => {
    useEffect(() => {
        const t = setTimeout(onDismiss, 4000);
        return () => clearTimeout(t);
    }, [onDismiss]);

    return (
        <div className="mt-3 flex items-center gap-2 rounded-md border border-green-200 bg-green-50 px-4 py-2.5">
            <LuCircleCheckBig className="h-4 w-4 flex-shrink-0 text-green-600" />
            <p className="text-sm text-green-800">{message}</p>
        </div>
    );
};

// ─── ActionItem ───────────────────────────────────────────────────────────────

interface ActionItemProps {
    icon: React.ReactNode;
    title: string;
    fieldSummary: string;
    badges: BadgeType[];
    children: React.ReactNode;
    defaultExpanded?: boolean;
}

const ActionItem = ({ icon, title, fieldSummary, badges, children, defaultExpanded = false }: ActionItemProps) => {
    const [expanded, setExpanded] = useState(defaultExpanded);

    return (
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
            <button
                type="button"
                className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-gray-50"
                onClick={() => setExpanded((e) => !e)}
            >
                <span className="text-muted-foreground flex-shrink-0">{icon}</span>
                <span className="flex-1 text-sm font-medium">{title}</span>
                <span className="text-muted-foreground mr-3 font-mono text-[11px]">{fieldSummary}</span>
                <div className="mr-2 flex items-center gap-1.5">
                    {badges.map((b) => (
                        <ControlBadge key={b} type={b} />
                    ))}
                </div>
                {expanded ? (
                    <LuChevronDown className="text-muted-foreground h-4 w-4 flex-shrink-0" />
                ) : (
                    <LuChevronRight className="text-muted-foreground h-4 w-4 flex-shrink-0" />
                )}
            </button>

            {expanded && <div className="border-t border-gray-100 bg-white px-4 py-4">{children}</div>}
        </div>
    );
};

// ─── ActionGroup ──────────────────────────────────────────────────────────────

interface ActionGroupProps {
    label: string;
    description?: string;
    children: React.ReactNode;
}

const ActionGroup = ({ label, description, children }: ActionGroupProps) => (
    <div className="rounded-xl border border-gray-200 bg-gray-50/40 p-6">
        <div className="mb-4">
            <p className="text-muted-foreground text-[11px] font-semibold tracking-widest uppercase">{label}</p>
            {description && <p className="text-muted-foreground mt-1 text-sm">{description}</p>}
        </div>
        <div className="space-y-2">{children}</div>
    </div>
);

// ─── TenantSelect ─────────────────────────────────────────────────────────────

interface TenantSelectProps {
    value: string;
    onChange: (v: string) => void;
    availableTenants: { tenantGUID: string; name: string }[];
}

const TenantSelect = ({ value, onChange, availableTenants }: TenantSelectProps) => (
    <div className="flex flex-col gap-1.5">
        <Label>Tenant</Label>
        <Select value={value} onValueChange={onChange}>
            <SelectTrigger className="w-[220px]">
                <SelectValue placeholder="Select a tenant..." />
            </SelectTrigger>
            <SelectContent>
                {availableTenants.map((tenant) => (
                    <SelectItem key={tenant.tenantGUID} value={tenant.tenantGUID}>
                        {tenant.name}
                    </SelectItem>
                ))}
            </SelectContent>
        </Select>
    </div>
);

// ─── AdvisorSelect ────────────────────────────────────────────────────────────

interface AdvisorSelectProps {
    value: string;
    onChange: (v: string) => void;
    advisors: TenantTeamMember[] | undefined;
    loading: boolean;
    tenantSelected: boolean;
}

const AdvisorSelect = ({ value, onChange, advisors, loading, tenantSelected }: AdvisorSelectProps) => (
    <div className="flex flex-col gap-1.5">
        <Label>Advisor</Label>
        <Select value={value} onValueChange={onChange} disabled={!tenantSelected || loading}>
            <SelectTrigger className="w-[220px]">
                <SelectValue placeholder="Select an advisor..." />
            </SelectTrigger>
            <SelectContent>
                {advisors?.map((advisor) => (
                    <SelectItem key={advisor.internalUserGUID} value={advisor.internalUserGUID}>
                        {advisor.name || advisor.email || 'Unnamed Advisor'}
                    </SelectItem>
                ))}
            </SelectContent>
        </Select>
    </div>
);

// ─── useAdvisorSelection ─────────────────────────────────────────────────────

const useAdvisorSelection = () => {
    const [selectedTenant, setSelectedTenant] = useState('');
    const [selectedAdvisor, setSelectedAdvisor] = useState('');
    const { data: team, isPending: advisorsLoading } = useTenantTeamQuery(selectedTenant || undefined);
    const advisors = team?.advisors;
    useEffect(() => {
        setSelectedAdvisor('');
    }, [selectedTenant]);
    useEffect(() => {
        if (selectedAdvisor && advisors && !advisors.some((a) => a.internalUserGUID === selectedAdvisor)) {
            setSelectedAdvisor('');
        }
    }, [advisors, selectedAdvisor]);
    return { selectedTenant, setSelectedTenant, selectedAdvisor, setSelectedAdvisor, advisors, advisorsLoading };
};

// ─── PlatformControls ─────────────────────────────────────────────────────────

export const PlatformControls = () => {
    const optimizationDisabled = useFeatureFlag(FeatureFlag.OPTIMIZATION_MAINTENANCE);
    const redtailExportEnabled = useFeatureFlag(FeatureFlag.REDTAIL_EXPORT);
    const featureFlagMutation = useToggleFeatureFlag(FeatureFlag.OPTIMIZATION_MAINTENANCE);
    const intakeMutation = useTriggerBoxIntake();
    const { availableTenants } = useAuthContext();
    const onboardingMutation = useTriggerHouseholdOnboarding();
    const singleAccountOnboardingMutation = useOnboardSingleAccountFromHouseholdOnboarding();
    const prospectOnboardingMutation = useTriggerProspectOnboarding();
    const importRedtailDataMutation = useImportRedtailData();
    const migrateEmailsMutation = useMigrateEmails();
    const migrateNotesMutation = useMigrateNotes();
    const migrateTextMessagesMutation = useMigrateTextMessages();
    const embedDocumentMutation = useEmbedDocument();
    const startIngestionMutation = useStartIngestion();
    const redtailExportMutation = useRedtailExportTenant();
    const moveAccountGroupMutation = useMoveAccountGroupAcrossHouseholds();
    const generateWritingStyleMutation = useGenerateWritingStyle();
    const regenerateMeetingNoteMutation = useRegenerateMeetingNote();

    // ── Confirmation visibility ────────────────────────────────────────────────
    const [pendingConfirm, setPendingConfirm] = useState<
        | 'killswitch-on'
        | 'killswitch-off'
        | 'intake'
        | 'ingestion'
        | 'move-account-group'
        | 'redtail-export'
        | 'regenerate-meeting-note'
        | null
    >(null);

    // ── Success visibility (auto-dismiss) ─────────────────────────────────────
    const [successVisible, setSuccessVisible] = useState<Record<string, boolean>>({});
    const showSuccess = (key: string) => setSuccessVisible((prev) => ({ ...prev, [key]: true }));
    const hideSuccess = (key: string) => setSuccessVisible((prev) => ({ ...prev, [key]: false }));

    // ── Kill switch ───────────────────────────────────────────────────────────
    const handleKillSwitchChange = (checked: boolean) => {
        setPendingConfirm(checked ? 'killswitch-on' : 'killswitch-off');
    };
    const confirmKillSwitch = () => {
        const isEnabled = pendingConfirm === 'killswitch-on';
        featureFlagMutation.mutate(
            { isEnabled },
            {
                onSuccess: () => {
                    setPendingConfirm(null);
                    showSuccess('killswitch');
                },
            },
        );
    };

    // ── Box intake ────────────────────────────────────────────────────────────
    const handleIntakeConfirm = () => {
        intakeMutation.mutate(undefined, {
            onSuccess: () => {
                setPendingConfirm(null);
                showSuccess('intake');
            },
            onError: () => {
                setPendingConfirm(null);
            },
        });
    };

    // ── Move account group ────────────────────────────────────────────────────
    const [moveAccountGroupGUID, setMoveAccountGroupGUID] = useState('');
    const [moveTargetHouseholdGUID, setMoveTargetHouseholdGUID] = useState('');
    const moveAccountGroupReady = !!moveAccountGroupGUID.trim() && !!moveTargetHouseholdGUID.trim();
    const handleMoveAccountGroupConfirm = () => {
        moveAccountGroupMutation.mutate(
            {
                accountGroupGUID: moveAccountGroupGUID.trim(),
                targetHouseholdGUID: moveTargetHouseholdGUID.trim(),
            },
            {
                onSuccess: () => {
                    setMoveAccountGroupGUID('');
                    setMoveTargetHouseholdGUID('');
                    setPendingConfirm(null);
                    showSuccess('move-account-group');
                },
            },
        );
    };

    // ── Regenerate meeting note ───────────────────────────────────────────────
    const [regenNoteGUID, setRegenNoteGUID] = useState('');
    const [regenTemplateType, setRegenTemplateType] = useState<NoteTemplateType>(NoteTemplateType.GENERAL);
    const handleRegenerateMeetingNoteConfirm = () => {
        regenerateMeetingNoteMutation.mutate(
            {
                noteGUID: regenNoteGUID.trim(),
                noteTemplateType: regenTemplateType,
            },
            {
                onSuccess: () => {
                    setRegenNoteGUID('');
                    setPendingConfirm(null);
                    showSuccess('regenerate-meeting-note');
                },
                onError: () => {
                    setPendingConfirm(null);
                },
            },
        );
    };

    // ── Household onboarding ──────────────────────────────────────────────────
    const {
        selectedTenant,
        setSelectedTenant,
        selectedAdvisor,
        setSelectedAdvisor,
        advisors: householdAdvisors,
        advisorsLoading: householdAdvisorsLoading,
    } = useAdvisorSelection();
    const onOnboardingSubmit = () => {
        if (!selectedAdvisor || !selectedTenant) return;
        onboardingMutation.mutate(
            { tenantGUID: selectedTenant, primaryAdvisorGUID: selectedAdvisor },
            { onSuccess: () => showSuccess('household-onboarding') },
        );
    };

    // ── Single account onboarding ─────────────────────────────────────────────
    const [singleAccountTenant, setSingleAccountTenant] = useState('');
    const [singleAccountHousehold, setSingleAccountHousehold] = useState('');
    const [singleAccountNumber, setSingleAccountNumber] = useState('');
    const { data: singleAccountHouseholds = [], isPending: singleAccountHouseholdsLoading } = useHouseholdsForTenant(
        singleAccountTenant || undefined,
    );
    const sortedSingleAccountHouseholds = [...singleAccountHouseholds].sort((a, b) =>
        getHouseholdName(a).localeCompare(getHouseholdName(b)),
    );
    useEffect(() => {
        setSingleAccountHousehold('');
    }, [singleAccountTenant]);
    const onSingleAccountOnboardingSubmit = () => {
        const accountNumber = singleAccountNumber.trim();
        if (!accountNumber || !singleAccountHousehold) return;
        singleAccountOnboardingMutation.mutate(
            { accountNumber, householdGUID: singleAccountHousehold },
            { onSuccess: () => showSuccess('single-account-onboarding') },
        );
    };

    // ── Prospect onboarding ───────────────────────────────────────────────────
    const {
        selectedTenant: selectedProspectTenant,
        setSelectedTenant: setSelectedProspectTenant,
        selectedAdvisor: selectedProspectAdvisor,
        setSelectedAdvisor: setSelectedProspectAdvisor,
        advisors: prospectAdvisors,
        advisorsLoading: prospectAdvisorsLoading,
    } = useAdvisorSelection();
    const onProspectOnboardingSubmit = () => {
        if (!selectedProspectAdvisor || !selectedProspectTenant) return;
        prospectOnboardingMutation.mutate(
            { tenantGUID: selectedProspectTenant, primaryAdvisorGUID: selectedProspectAdvisor },
            { onSuccess: () => showSuccess('prospect-onboarding') },
        );
    };

    // ── Redtail import ────────────────────────────────────────────────────────
    const {
        selectedTenant: selectedEmailImportTenant,
        setSelectedTenant: setSelectedEmailImportTenant,
        selectedAdvisor: selectedEmailImportAdvisor,
        setSelectedAdvisor: setSelectedEmailImportAdvisor,
        advisors: emailImportAdvisors,
        advisorsLoading: emailImportAdvisorsLoading,
    } = useAdvisorSelection();
    const onImportRedtailDataSubmit = () => {
        if (!selectedEmailImportAdvisor || !selectedEmailImportTenant) return;
        importRedtailDataMutation.mutate(
            { tenantGUID: selectedEmailImportTenant, advisorGUID: selectedEmailImportAdvisor },
            { onSuccess: () => showSuccess('redtail-import') },
        );
    };

    // ── Writing style generation ───────────────────────────────────────────────
    const {
        selectedTenant: selectedWritingStyleTenant,
        setSelectedTenant: setSelectedWritingStyleTenant,
        selectedAdvisor: selectedWritingStyleAdvisor,
        setSelectedAdvisor: setSelectedWritingStyleAdvisor,
        advisors: writingStyleAdvisors,
        advisorsLoading: writingStyleAdvisorsLoading,
    } = useAdvisorSelection();
    const onGenerateWritingStyleSubmit = () => {
        if (!selectedWritingStyleAdvisor) return;
        generateWritingStyleMutation.mutate(selectedWritingStyleAdvisor, {
            onSuccess: () => showSuccess('writing-style-generation'),
        });
    };

    // ── Knowledge base ────────────────────────────────────────────────────────
    const [selectedEmailMigrationTenant, setSelectedEmailMigrationTenant] = useState('');
    const [selectedNoteMigrationTenant, setSelectedNoteMigrationTenant] = useState('');
    const [embedEntityType, setEmbedEntityType] = useState('');
    const [embedEntityGUID, setEmbedEntityGUID] = useState('');

    const handleIngestionConfirm = () => {
        startIngestionMutation.mutate(undefined, {
            onSuccess: () => {
                setPendingConfirm(null);
                showSuccess('start-ingestion');
            },
            onError: () => {
                setPendingConfirm(null);
            },
        });
    };

    // ── Nylas event sync ──────────────────────────────────────────────────────
    const { data: calendars = [], isPending: calendarsLoading } = useListCalendars();
    const syncCalendarMutation = useSyncCalendar();
    const syncCalendarEventMutation = useSyncCalendarEvent();
    const [selectedCalendarGUID, setSelectedCalendarGUID] = useState<GUID<Calendar>>('');
    const [syncLogLine, setSyncLogLine] = useState('');
    const [syncGrantId, setSyncGrantId] = useState('');
    const [syncNylasEventId, setSyncNylasEventId] = useState('');

    const parseEventSyncLog = (line: string) => {
        // Pattern 1: "Ignored notification: id={}, type={}, grantId={}, masterEventId={}."
        const p1 = line.match(/grantId=([^,\s]+),\s*masterEventId=([^.,\s]+)/);
        if (p1) {
            setSyncGrantId(p1[1]);
            setSyncNylasEventId(p1[2]);
            return;
        }
        // Pattern 2: "Error syncing Event(id={}) in Calendar(grantId={})"
        const p2 = line.match(/Error syncing Event\(id=([^)]+)\) in Calendar\(grantId=([^)]+)\)/);
        if (p2) {
            setSyncNylasEventId(p2[1]);
            setSyncGrantId(p2[2]);
        }
    };

    // ── Redtail export ────────────────────────────────────────────────────────
    const [selectedRedtailExportTenant, setSelectedRedtailExportTenant] = useState('');
    const handleRedtailExportConfirm = () => {
        redtailExportMutation.mutate(
            { tenantGUID: selectedRedtailExportTenant },
            {
                onSuccess: () => {
                    setPendingConfirm(null);
                    showSuccess('redtail-export');
                },
                onError: () => {
                    setPendingConfirm(null);
                },
            },
        );
    };

    // ── Helpers ───────────────────────────────────────────────────────────────
    // ── Render ────────────────────────────────────────────────────────────────

    return (
        <div className="flex flex-col gap-6">
            {/* ── GROUP 1: Platform operations ────────────────────────────── */}
            <ActionGroup
                label="Platform operations"
                description="System-wide actions. No tenant scope. Affect all users or global data."
            >
                {/* Kill switch */}
                <ActionItem
                    icon={<LuSettings2 className="h-4 w-4" />}
                    title="Optimization Kill Switch"
                    fieldSummary="toggle"
                    badges={['confirm-required']}
                >
                    <div className="flex items-center gap-4">
                        <Switch
                            checked={optimizationDisabled}
                            onCheckedChange={handleKillSwitchChange}
                            disabled={featureFlagMutation.isPending}
                        />
                        <span className="text-muted-foreground text-sm">
                            {optimizationDisabled
                                ? 'Optimization is currently OFF — Lab shows maintenance page'
                                : 'Optimization is currently ON — Lab is active'}
                        </span>
                    </div>

                    {pendingConfirm === 'killswitch-on' && (
                        <ConfirmBar
                            message="This will replace the optimization page with a maintenance page for all users. Existing optimizations will still run."
                            onConfirm={confirmKillSwitch}
                            onCancel={() => setPendingConfirm(null)}
                            isPending={featureFlagMutation.isPending}
                        />
                    )}
                    {pendingConfirm === 'killswitch-off' && (
                        <ConfirmBar
                            message="This will re-enable the optimization Lab for all users."
                            onConfirm={confirmKillSwitch}
                            onCancel={() => setPendingConfirm(null)}
                            isPending={featureFlagMutation.isPending}
                        />
                    )}
                    {successVisible['killswitch'] && (
                        <SuccessBar message="Kill switch updated." onDismiss={() => hideSuccess('killswitch')} />
                    )}
                </ActionItem>

                {/* Box intake */}
                <ActionItem
                    icon={<LuPackageOpen className="h-4 w-4" />}
                    title="Box Intake Tax Trigger"
                    fieldSummary="no inputs"
                    badges={['irreversible']}
                >
                    <p className="text-muted-foreground mb-3 text-sm">
                        Triggers intake on the{' '}
                        <code className="rounded bg-gray-100 px-1 py-0.5 font-mono text-xs">/intake/tax</code> folder in
                        Box. Naming convention:{' '}
                        <code className="rounded bg-gray-100 px-1 py-0.5 font-mono text-xs">
                            IBACCOUNTID.YEAR.TYPE.EXT
                        </code>
                        .
                    </p>

                    {pendingConfirm !== 'intake' && (
                        <Button onClick={() => setPendingConfirm('intake')} disabled={intakeMutation.isPending}>
                            {intakeMutation.isPending && <LuLoader className="mr-2 h-4 w-4 animate-spin" />}
                            Run Intake
                        </Button>
                    )}

                    {pendingConfirm === 'intake' && (
                        <ConfirmBar
                            message="This will trigger a full Box intake run on /intake/tax. Cannot be undone. Continue?"
                            onConfirm={handleIntakeConfirm}
                            onCancel={() => setPendingConfirm(null)}
                            isPending={intakeMutation.isPending}
                        />
                    )}
                    {successVisible['intake'] && (
                        <SuccessBar message="Intake triggered successfully." onDismiss={() => hideSuccess('intake')} />
                    )}
                </ActionItem>

                {/* Move account group */}
                <ActionItem
                    icon={<LuArrowRightLeft className="h-4 w-4" />}
                    title="Move Account Group Across Households"
                    fieldSummary="accountGroupGUID + target householdGUID"
                    badges={['irreversible']}
                >
                    <div className="flex flex-wrap items-end gap-4">
                        <div className="flex flex-col gap-1.5">
                            <Label>Account Group GUID</Label>
                            <Input
                                className="w-[300px] font-mono"
                                value={moveAccountGroupGUID}
                                onChange={(e) => setMoveAccountGroupGUID(e.target.value)}
                                placeholder="Account group GUID..."
                            />
                        </div>
                        <div className="flex flex-col gap-1.5">
                            <Label>Target Household GUID</Label>
                            <Input
                                className="w-[300px] font-mono"
                                value={moveTargetHouseholdGUID}
                                onChange={(e) => setMoveTargetHouseholdGUID(e.target.value)}
                                placeholder="Target household GUID..."
                            />
                        </div>
                        {pendingConfirm !== 'move-account-group' && (
                            <Button
                                onClick={() => setPendingConfirm('move-account-group')}
                                disabled={!moveAccountGroupReady || moveAccountGroupMutation.isPending}
                            >
                                {moveAccountGroupMutation.isPending && (
                                    <LuLoader className="mr-2 h-4 w-4 animate-spin" />
                                )}
                                Move
                            </Button>
                        )}
                    </div>

                    {pendingConfirm === 'move-account-group' && (
                        <ConfirmBar
                            message={`This will move account group ${moveAccountGroupGUID.trim()} and all its accounts to household ${moveTargetHouseholdGUID.trim()}. Cannot be undone.`}
                            onConfirm={handleMoveAccountGroupConfirm}
                            onCancel={() => setPendingConfirm(null)}
                            isPending={moveAccountGroupMutation.isPending}
                        />
                    )}
                    {successVisible['move-account-group'] && (
                        <SuccessBar
                            message="Account group moved successfully."
                            onDismiss={() => hideSuccess('move-account-group')}
                        />
                    )}
                </ActionItem>

                {/* Regenerate meeting note */}
                <ActionItem
                    icon={<LuStickyNote className="h-4 w-4" />}
                    title="Regenerate Meeting Note"
                    fieldSummary="noteGUID + template"
                    badges={['per-document', 'confirm-required']}
                >
                    <p className="text-muted-foreground mb-3 text-sm">
                        Re-runs AI note generation from the stored meeting transcript. Use it when transcript processing
                        failed (e.g. a Bedrock outage) and the note only contains the error placeholder. Find the note
                        via its meeting: the note is attached to the failed meeting bot. Notes without a household
                        ignore the selected template and use the general non-client template.
                    </p>
                    <div className="flex flex-wrap items-end gap-4">
                        <div className="flex flex-col gap-1.5">
                            <Label>Note GUID</Label>
                            <Input
                                className="w-[300px] font-mono"
                                value={regenNoteGUID}
                                onChange={(e) => setRegenNoteGUID(e.target.value)}
                                placeholder="Note GUID..."
                            />
                        </div>
                        <div className="flex flex-col gap-1.5">
                            <Label>Template</Label>
                            <Select
                                value={regenTemplateType}
                                onValueChange={(v) => setRegenTemplateType(v as NoteTemplateType)}
                            >
                                <SelectTrigger className="w-[220px]">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {Object.values(NoteTemplateType).map((type) => (
                                        <SelectItem key={type} value={type}>
                                            {type}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        {pendingConfirm !== 'regenerate-meeting-note' && (
                            <Button
                                onClick={() => setPendingConfirm('regenerate-meeting-note')}
                                disabled={!regenNoteGUID.trim() || regenerateMeetingNoteMutation.isPending}
                            >
                                {regenerateMeetingNoteMutation.isPending && (
                                    <LuLoader className="mr-2 h-4 w-4 animate-spin" />
                                )}
                                Regenerate
                            </Button>
                        )}
                    </div>

                    {pendingConfirm === 'regenerate-meeting-note' && (
                        <ConfirmBar
                            message={`This will overwrite the content of note ${regenNoteGUID.trim()} with freshly generated meeting notes. The current note content is lost. Continue?`}
                            onConfirm={handleRegenerateMeetingNoteConfirm}
                            onCancel={() => setPendingConfirm(null)}
                            isPending={regenerateMeetingNoteMutation.isPending}
                        />
                    )}
                    {successVisible['regenerate-meeting-note'] && (
                        <SuccessBar
                            message="Meeting note regenerated from the stored transcript."
                            onDismiss={() => hideSuccess('regenerate-meeting-note')}
                        />
                    )}
                </ActionItem>
            </ActionGroup>

            {/* ── GROUP 2: Onboarding ──────────────────────────────────────── */}
            <ActionGroup
                label="Onboarding"
                description="Tenant-scoped client setup operations. All controls require selecting a tenant."
            >
                {/* Household onboarding */}
                <ActionItem
                    icon={<LuUsers className="h-4 w-4" />}
                    title="Household Onboarding"
                    fieldSummary="tenant → advisor"
                    badges={['per-tenant']}
                >
                    <div className="flex flex-wrap items-end gap-4">
                        <TenantSelect
                            value={selectedTenant}
                            onChange={setSelectedTenant}
                            availableTenants={availableTenants}
                        />
                        <AdvisorSelect
                            value={selectedAdvisor}
                            onChange={setSelectedAdvisor}
                            advisors={householdAdvisors}
                            loading={householdAdvisorsLoading}
                            tenantSelected={!!selectedTenant}
                        />
                        <Button
                            onClick={onOnboardingSubmit}
                            disabled={!selectedAdvisor || !selectedTenant || onboardingMutation.isPending}
                        >
                            {onboardingMutation.isPending && <LuLoader className="mr-2 h-4 w-4 animate-spin" />}
                            Run Onboarding
                        </Button>
                    </div>
                    {successVisible['household-onboarding'] && (
                        <SuccessBar
                            message="Household onboarding triggered."
                            onDismiss={() => hideSuccess('household-onboarding')}
                        />
                    )}
                </ActionItem>

                {/* Single account onboarding */}
                <ActionItem
                    icon={<LuUsers className="h-4 w-4" />}
                    title="Single Account Onboarding"
                    fieldSummary="tenant → household + account number"
                    badges={['per-tenant']}
                >
                    <div className="flex flex-wrap items-end gap-4">
                        <TenantSelect
                            value={singleAccountTenant}
                            onChange={setSingleAccountTenant}
                            availableTenants={availableTenants}
                        />
                        <div className="flex flex-col gap-1.5">
                            <Label>Household</Label>
                            <Select
                                value={singleAccountHousehold}
                                onValueChange={setSingleAccountHousehold}
                                disabled={!singleAccountTenant || singleAccountHouseholdsLoading}
                            >
                                <SelectTrigger className="w-[260px]">
                                    <SelectValue placeholder="Select a household..." />
                                </SelectTrigger>
                                <SelectContent>
                                    {sortedSingleAccountHouseholds.map((hh) => (
                                        <SelectItem key={hh.globalAccountGUID} value={hh.globalAccountGUID}>
                                            {getHouseholdName(hh) || 'Unnamed Household'}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="flex flex-col gap-1.5">
                            <Label>Account Number</Label>
                            <Input
                                className="w-[180px]"
                                value={singleAccountNumber}
                                onChange={(e) => setSingleAccountNumber(e.target.value)}
                                placeholder="Account number..."
                            />
                        </div>
                        <Button
                            onClick={onSingleAccountOnboardingSubmit}
                            disabled={
                                !singleAccountNumber.trim() ||
                                !singleAccountTenant ||
                                !singleAccountHousehold ||
                                singleAccountOnboardingMutation.isPending
                            }
                        >
                            {singleAccountOnboardingMutation.isPending && (
                                <LuLoader className="mr-2 h-4 w-4 animate-spin" />
                            )}
                            Run Onboarding
                        </Button>
                    </div>
                    {successVisible['single-account-onboarding'] && (
                        <SuccessBar
                            message="Single account onboarding triggered."
                            onDismiss={() => hideSuccess('single-account-onboarding')}
                        />
                    )}
                </ActionItem>

                {/* Prospect onboarding */}
                <ActionItem
                    icon={<LuUsers className="h-4 w-4" />}
                    title="Prospect Onboarding"
                    fieldSummary="tenant → advisor"
                    badges={['per-tenant']}
                >
                    <div className="flex flex-wrap items-end gap-4">
                        <TenantSelect
                            value={selectedProspectTenant}
                            onChange={setSelectedProspectTenant}
                            availableTenants={availableTenants}
                        />
                        <AdvisorSelect
                            value={selectedProspectAdvisor}
                            onChange={setSelectedProspectAdvisor}
                            advisors={prospectAdvisors}
                            loading={prospectAdvisorsLoading}
                            tenantSelected={!!selectedProspectTenant}
                        />
                        <Button
                            onClick={onProspectOnboardingSubmit}
                            disabled={
                                !selectedProspectAdvisor ||
                                !selectedProspectTenant ||
                                prospectOnboardingMutation.isPending
                            }
                        >
                            {prospectOnboardingMutation.isPending && <LuLoader className="mr-2 h-4 w-4 animate-spin" />}
                            Run Onboarding
                        </Button>
                    </div>
                    {successVisible['prospect-onboarding'] && (
                        <SuccessBar
                            message="Prospect onboarding triggered."
                            onDismiss={() => hideSuccess('prospect-onboarding')}
                        />
                    )}
                </ActionItem>

                {/* Redtail import */}
                <ActionItem
                    icon={<LuFileInput className="h-4 w-4" />}
                    title="Redtail Import (Notes, Activities, Emails)"
                    fieldSummary="tenant → advisor"
                    badges={['per-tenant']}
                >
                    <div className="flex flex-wrap items-end gap-4">
                        <TenantSelect
                            value={selectedEmailImportTenant}
                            onChange={setSelectedEmailImportTenant}
                            availableTenants={availableTenants}
                        />
                        <AdvisorSelect
                            value={selectedEmailImportAdvisor}
                            onChange={setSelectedEmailImportAdvisor}
                            advisors={emailImportAdvisors}
                            loading={emailImportAdvisorsLoading}
                            tenantSelected={!!selectedEmailImportTenant}
                        />
                        <Button
                            onClick={onImportRedtailDataSubmit}
                            disabled={
                                !selectedEmailImportAdvisor ||
                                !selectedEmailImportTenant ||
                                importRedtailDataMutation.isPending
                            }
                        >
                            {importRedtailDataMutation.isPending && <LuLoader className="mr-2 h-4 w-4 animate-spin" />}
                            Import Redtail Data
                        </Button>
                    </div>
                    {successVisible['redtail-import'] && (
                        <SuccessBar
                            message="Redtail import triggered."
                            onDismiss={() => hideSuccess('redtail-import')}
                        />
                    )}
                </ActionItem>

                {/* Writing style generation */}
                <ActionItem
                    icon={<LuPenTool className="h-4 w-4" />}
                    title="Writing Style Generation"
                    fieldSummary="tenant → advisor"
                    badges={['per-tenant']}
                >
                    <p className="text-muted-foreground mb-3 text-sm">
                        Generates a writing style profile for an advisor by analyzing their recent sent emails. The
                        profile is used to personalize AI-generated content.
                    </p>
                    <div className="flex flex-wrap items-end gap-4">
                        <TenantSelect
                            value={selectedWritingStyleTenant}
                            onChange={setSelectedWritingStyleTenant}
                            availableTenants={availableTenants}
                        />
                        <AdvisorSelect
                            value={selectedWritingStyleAdvisor}
                            onChange={setSelectedWritingStyleAdvisor}
                            advisors={writingStyleAdvisors}
                            loading={writingStyleAdvisorsLoading}
                            tenantSelected={!!selectedWritingStyleTenant}
                        />
                        <Button
                            onClick={onGenerateWritingStyleSubmit}
                            disabled={
                                !selectedWritingStyleAdvisor ||
                                !selectedWritingStyleTenant ||
                                generateWritingStyleMutation.isPending
                            }
                        >
                            {generateWritingStyleMutation.isPending && (
                                <LuLoader className="mr-2 h-4 w-4 animate-spin" />
                            )}
                            Generate Writing Style
                        </Button>
                    </div>
                    {successVisible['writing-style-generation'] && (
                        <SuccessBar
                            message="Writing style generated successfully."
                            onDismiss={() => hideSuccess('writing-style-generation')}
                        />
                    )}
                </ActionItem>

                {/* Redtail export (feature-flagged) */}
                {redtailExportEnabled && (
                    <ActionItem
                        icon={<LuFileOutput className="h-4 w-4" />}
                        title="Redtail Export"
                        fieldSummary="tenant"
                        badges={['irreversible', 'per-tenant']}
                    >
                        <p className="text-muted-foreground mb-3 text-sm">
                            One-time export of all client data for a tenant into Redtail CRM. Pushes Contacts,
                            Addresses, Emails, Phones, Family links, Accounts, Notes, Tasks, and Opportunities.
                        </p>
                        <div className="flex flex-wrap items-end gap-4">
                            <TenantSelect
                                value={selectedRedtailExportTenant}
                                onChange={setSelectedRedtailExportTenant}
                                availableTenants={availableTenants}
                            />
                            {pendingConfirm !== 'redtail-export' && (
                                <Button
                                    onClick={() => setPendingConfirm('redtail-export')}
                                    disabled={!selectedRedtailExportTenant || redtailExportMutation.isPending}
                                >
                                    {redtailExportMutation.isPending && (
                                        <LuLoader className="mr-2 h-4 w-4 animate-spin" />
                                    )}
                                    Run Export
                                </Button>
                            )}
                        </div>
                        {pendingConfirm === 'redtail-export' && (
                            <ConfirmBar
                                message="This will export all client data for the selected tenant into Redtail CRM. This is a one-time operation and cannot be reversed."
                                onConfirm={handleRedtailExportConfirm}
                                onCancel={() => setPendingConfirm(null)}
                                isPending={redtailExportMutation.isPending}
                            />
                        )}
                        {successVisible['redtail-export'] && (
                            <SuccessBar
                                message="Redtail export triggered."
                                onDismiss={() => hideSuccess('redtail-export')}
                            />
                        )}
                    </ActionItem>
                )}
            </ActionGroup>

            {/* ── GROUP 3: Nylas event sync ────────────────────────────────── */}
            <ActionGroup label="Nylas event sync" description="Manually trigger Nylas calendar and event syncs.">
                {/* Sync calendar */}
                <ActionItem
                    icon={<LuCalendarSync className="h-4 w-4" />}
                    title="Sync Calendar"
                    fieldSummary="calendar"
                    badges={['routine']}
                >
                    <div className="flex flex-wrap items-end gap-4">
                        <div className="flex flex-col gap-1.5">
                            <Label>Calendar</Label>
                            <Select
                                value={selectedCalendarGUID}
                                onValueChange={setSelectedCalendarGUID}
                                disabled={calendarsLoading}
                            >
                                <SelectTrigger className="w-[320px]">
                                    <SelectValue placeholder="Select a calendar..." />
                                </SelectTrigger>
                                <SelectContent>
                                    {calendars.map((cal) => (
                                        <SelectItem key={cal.calendarGUID} value={cal.calendarGUID}>
                                            {cal.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <Button
                            onClick={() =>
                                syncCalendarMutation.mutate(selectedCalendarGUID, {
                                    onSuccess: () => showSuccess('sync-calendar'),
                                })
                            }
                            disabled={!selectedCalendarGUID || syncCalendarMutation.isPending}
                        >
                            {syncCalendarMutation.isPending && <LuLoader className="mr-2 h-4 w-4 animate-spin" />}
                            Sync
                        </Button>
                    </div>
                    {successVisible['sync-calendar'] && (
                        <SuccessBar message="Calendar sync triggered." onDismiss={() => hideSuccess('sync-calendar')} />
                    )}
                </ActionItem>

                {/* Sync Event */}
                <ActionItem
                    icon={<LuCalendarSync className="h-4 w-4" />}
                    title="Sync Event"
                    fieldSummary="grant ID + event ID"
                    badges={['routine']}
                >
                    <div className="flex flex-col gap-4">
                        <div className="flex flex-col gap-1.5">
                            <Label>Paste log line</Label>
                            <Textarea
                                className="font-mono text-xs"
                                rows={2}
                                value={syncLogLine}
                                onChange={(e) => {
                                    setSyncLogLine(e.target.value);
                                    parseEventSyncLog(e.target.value);
                                }}
                                placeholder={
                                    'Ignored notification: ... grantId=<id>, masterEventId=<id>.\nor: Error syncing event <id> in calendar <id>'
                                }
                            />
                        </div>
                        <div className="flex flex-wrap items-end gap-4">
                            <div className="flex flex-col gap-1.5">
                                <Label>Grant ID</Label>
                                <Input
                                    className="w-[280px] font-mono"
                                    value={syncGrantId}
                                    onChange={(e) => setSyncGrantId(e.target.value)}
                                    placeholder="Nylas grant ID..."
                                />
                            </div>
                            <div className="flex flex-col gap-1.5">
                                <Label>Nylas Event ID</Label>
                                <Input
                                    className="w-[280px] font-mono"
                                    value={syncNylasEventId}
                                    onChange={(e) => setSyncNylasEventId(e.target.value)}
                                    placeholder="Nylas event ID..."
                                />
                            </div>
                            <Button
                                onClick={() =>
                                    syncCalendarEventMutation.mutate(
                                        { grantId: syncGrantId.trim(), nylasEventId: syncNylasEventId.trim() },
                                        { onSuccess: () => showSuccess('sync-calendar-event') },
                                    )
                                }
                                disabled={
                                    !syncGrantId.trim() ||
                                    !syncNylasEventId.trim() ||
                                    syncCalendarEventMutation.isPending
                                }
                            >
                                {syncCalendarEventMutation.isPending && (
                                    <LuLoader className="mr-2 h-4 w-4 animate-spin" />
                                )}
                                Sync Event
                            </Button>
                        </div>
                    </div>
                    {successVisible['sync-calendar-event'] && (
                        <SuccessBar
                            message="Event sync triggered."
                            onDismiss={() => hideSuccess('sync-calendar-event')}
                        />
                    )}
                </ActionItem>
            </ActionGroup>

            {/* ── GROUP 4: Knowledge base ─────────────────────────────────── */}
            <ActionGroup
                label="Knowledge base"
                description="Knowledge base operations. Email and note migration are per tenant. Text message migration, embed, and ingestion are platform-wide."
            >
                {/* Embed document */}
                <ActionItem
                    icon={<LuZap className="h-4 w-4" />}
                    title="Embed Document"
                    fieldSummary="entity type + entity GUID"
                    badges={['per-document']}
                >
                    <p className="text-muted-foreground mb-3 text-sm">
                        Embeds a single document into the Bedrock knowledge base.
                    </p>
                    <div className="flex flex-wrap items-end gap-4">
                        <div className="flex flex-col gap-1.5">
                            <Label>Entity Type</Label>
                            <Select value={embedEntityType} onValueChange={setEmbedEntityType}>
                                <SelectTrigger className="w-[180px]">
                                    <SelectValue placeholder="Select type..." />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="EMAIL">Email</SelectItem>
                                    <SelectItem value="NOTE">Note</SelectItem>
                                    <SelectItem value="TEXT_MESSAGE">Text Message</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="flex flex-col gap-1.5">
                            <Label>Entity GUID</Label>
                            <Input
                                className="w-[300px] font-mono"
                                value={embedEntityGUID}
                                onChange={(e) => setEmbedEntityGUID(e.target.value)}
                                placeholder="Entity GUID..."
                            />
                        </div>
                        <Button
                            onClick={() =>
                                embedDocumentMutation.mutate(
                                    { entityType: embedEntityType, entityGUID: embedEntityGUID },
                                    { onSuccess: () => showSuccess('embed-document') },
                                )
                            }
                            disabled={!embedEntityType || !embedEntityGUID.trim() || embedDocumentMutation.isPending}
                        >
                            {embedDocumentMutation.isPending && <LuLoader className="mr-2 h-4 w-4 animate-spin" />}
                            Embed
                        </Button>
                    </div>
                    {successVisible['embed-document'] && (
                        <SuccessBar
                            message="Document embedded successfully."
                            onDismiss={() => hideSuccess('embed-document')}
                        />
                    )}
                </ActionItem>

                {/* Start ingestion */}
                <ActionItem
                    icon={<LuPlay className="h-4 w-4" />}
                    title="Start Ingestion"
                    fieldSummary="no inputs"
                    badges={['long-running']}
                >
                    <p className="text-muted-foreground mb-3 text-sm">
                        Triggers a full ingestion run into the Bedrock knowledge base. May take significant time — avoid
                        running during active deploys.
                    </p>

                    {pendingConfirm !== 'ingestion' && (
                        <Button
                            onClick={() => setPendingConfirm('ingestion')}
                            disabled={startIngestionMutation.isPending}
                        >
                            {startIngestionMutation.isPending && <LuLoader className="mr-2 h-4 w-4 animate-spin" />}
                            Start Ingestion
                        </Button>
                    )}

                    {pendingConfirm === 'ingestion' && (
                        <ConfirmBar
                            message="This will trigger a full Bedrock ingestion run. The operation may take significant time and cannot be interrupted. Continue?"
                            onConfirm={handleIngestionConfirm}
                            onCancel={() => setPendingConfirm(null)}
                            isPending={startIngestionMutation.isPending}
                        />
                    )}
                    {successVisible['start-ingestion'] && (
                        <SuccessBar message="Ingestion started." onDismiss={() => hideSuccess('start-ingestion')} />
                    )}
                </ActionItem>

                {/* Email migration */}
                <ActionItem
                    icon={<LuMail className="h-4 w-4" />}
                    title="Email Migration"
                    fieldSummary="tenant"
                    badges={['per-tenant']}
                >
                    <p className="text-muted-foreground mb-3 text-sm">
                        Migrates all emails for the selected tenant to knowledge base storage. Run per tenant to avoid
                        interruption by deploys. Does not start ingestion. Start it manually.
                    </p>
                    <div className="flex flex-wrap items-end gap-4">
                        <TenantSelect
                            value={selectedEmailMigrationTenant}
                            onChange={setSelectedEmailMigrationTenant}
                            availableTenants={availableTenants}
                        />
                        <Button
                            onClick={() =>
                                migrateEmailsMutation.mutate(
                                    { tenantGUID: selectedEmailMigrationTenant },
                                    { onSuccess: () => showSuccess('email-migration') },
                                )
                            }
                            disabled={!selectedEmailMigrationTenant || migrateEmailsMutation.isPending}
                        >
                            {migrateEmailsMutation.isPending && <LuLoader className="mr-2 h-4 w-4 animate-spin" />}
                            Run Migration
                        </Button>
                    </div>
                    {successVisible['email-migration'] && (
                        <SuccessBar
                            message="Email migration triggered."
                            onDismiss={() => hideSuccess('email-migration')}
                        />
                    )}
                </ActionItem>

                {/* Note migration */}
                <ActionItem
                    icon={<LuStickyNote className="h-4 w-4" />}
                    title="Note Migration"
                    fieldSummary="tenant"
                    badges={['per-tenant']}
                >
                    <p className="text-muted-foreground mb-3 text-sm">
                        Migrates all notes for the selected tenant to knowledge base storage. Run per tenant to avoid
                        interruption by deploys. Does not start ingestion. Start it manually.
                    </p>
                    <div className="flex flex-wrap items-end gap-4">
                        <TenantSelect
                            value={selectedNoteMigrationTenant}
                            onChange={setSelectedNoteMigrationTenant}
                            availableTenants={availableTenants}
                        />
                        <Button
                            onClick={() =>
                                migrateNotesMutation.mutate(
                                    { tenantGUID: selectedNoteMigrationTenant },
                                    { onSuccess: () => showSuccess('note-migration') },
                                )
                            }
                            disabled={!selectedNoteMigrationTenant || migrateNotesMutation.isPending}
                        >
                            {migrateNotesMutation.isPending && <LuLoader className="mr-2 h-4 w-4 animate-spin" />}
                            Run Migration
                        </Button>
                    </div>
                    {successVisible['note-migration'] && (
                        <SuccessBar
                            message="Note migration triggered."
                            onDismiss={() => hideSuccess('note-migration')}
                        />
                    )}
                </ActionItem>

                {/* Text message migration */}
                <ActionItem
                    icon={<LuMessageSquare className="h-4 w-4" />}
                    title="Text Message Migration"
                    fieldSummary="no inputs"
                    badges={['all-tenants', 'long-running']}
                >
                    <p className="text-muted-foreground mb-3 text-sm">
                        Migrates all text messages across every tenant to knowledge base storage, then starts a single
                        ingestion run. May take significant time — avoid running during active deploys.
                    </p>
                    <Button
                        onClick={() =>
                            migrateTextMessagesMutation.mutate(undefined, {
                                onSuccess: () => showSuccess('text-message-migration'),
                            })
                        }
                        disabled={migrateTextMessagesMutation.isPending}
                    >
                        {migrateTextMessagesMutation.isPending && <LuLoader className="mr-2 h-4 w-4 animate-spin" />}
                        Run Migration
                    </Button>
                    {successVisible['text-message-migration'] && (
                        <SuccessBar
                            message="Text message migration triggered."
                            onDismiss={() => hideSuccess('text-message-migration')}
                        />
                    )}
                </ActionItem>
            </ActionGroup>
        </div>
    );
};
